<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">{{ $tc('LBL_ORDER', 2) }}</h1>
                <!-- Breadcrumb -->
                <nav
                    class="text-sm text-gray-500 mb-6"
                    aria-label="Breadcrumb"
                >
                    <ol class="flex items-center space-x-2">
                        <li class="flex items-center">
                            <inertia-link
                                :href="route('landing')"
                                class="text-green-600 hover:underline"
                            >
                                {{ $t('LBL_HOME') }}
                            </inertia-link>
                            <svg
                                class="w-3 h-3 mx-2 text-green-400"
                                fill="currentColor"
                                viewBox="0 0 320 512"
                            >
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                />
                            </svg>
                        </li>
                        <li class="text-gray-700">{{ pageTitle }} {{ $tc('LBL_ORDER', 1) }}</li>
                    </ol>
                </nav>
            </div>

            <!-- Table -->
            <div
                class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-visible"
            >
                <!-- Search & Action Bar -->
                <div class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap">
                    <!-- Search Box -->
                    <div class="relative w-full sm:w-auto">
                        <label
                            for="table-search"
                            class="sr-only"
                            >Search</label
                        >
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                                class="w-5 h-5 text-gray-500 dark:text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <t-input
                            id="order-search-box"
                            v-model="search"
                            variant="success"
                            class="w-full sm:w-80 pl-10 pr-4 py-2.5 text-sm rounded-md border border-gray-300 focus:ring-green-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            placeholder="Search for order"
                            @keypress.enter="fetchPageData()"
                        />
                    </div>

                    <!-- Action Buttons -->
                    <!-- Payment Method -->
                    <div class="flex flex-wrap gap-2">
                        <label
                            for="table-search"
                            class="flex items-center mr-2 text-sm font-semibold"
                        >
                            Payment Method
                        </label>
                        <t-radio-group
                            v-model="paymentType"
                            :classes="{
                                groupWrapper: 'flex justify-around items-center gap-4',
                                label: 'ml-2 text-gray-700 uppercase text-sm font-semibold',
                                input: 'text-green-500 transition duration-100 ease-in-out border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none focus:ring-opacity-50 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed',
                            }"
                            :options="['BOTH', 'COD', 'ACCOUNT']"
                        />
                    </div>
                </div>

                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                >
                    <div class="overflow-x-auto">
                        <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <th class="px-4 py-3">#</th>
                                    <th class="px-4 py-3">ORDER NUMBER</th>
                                    <th class="px-4 py-3">NAME</th>
                                    <th class="px-4 py-3">AMOUNT</th>
                                    <th class="px-4 py-3">DATE</th>
                                    <th class="px-4 py-3">TERMS OF PAYMENT</th>
                                    <th class="px-4 py-3">ACTION</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(row, rowIndex) in bills.data">
                                    <tr
                                        class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200"
                                    >
                                        <td class="px-4 py-3">{{ rowIndex + 1 + (currentPage - 1) * perPage }}</td>
                                        <td class="px-4 py-3 whitespace-nowrap text-gray-800 dark:text-gray-200">
                                            {{ row.bill_number }}
                                        </td>
                                        <td class="px-4 py-3 dark:text-gray-300">
                                            {{ row.user.name }}
                                        </td>
                                        <td class="px-4 py-3 text-green-600 font-medium">
                                            {{ config(common.currency) }} {{ getTotalAmount(row.transactions) }}
                                        </td>
                                        <td class="px-4 py-3 dark:text-gray-300">
                                            {{ row.created_at | dateLabel }}
                                        </td>
                                        <td class="px-4 py-3 uppercase font-semibold text-blue-500">
                                            {{ row.payment_method }}
                                        </td>
                                        <td class="px-4 py-3">
                                            <div v-if="deliveryStatus == 'Placed'">
                                                <t-button
                                                    variant="success"
                                                    size="sm"
                                                    @click="goto('account.show', { orderId: row.bill_number })"
                                                >
                                                    Token
                                                </t-button>
                                            </div>
                                            <div v-else-if="deliveryStatus == 'Packed'">
                                                <t-button
                                                    variant="success"
                                                    size="sm"
                                                    @click="goto('invoice.dispatch.show', { billNo: row.bill_number })"
                                                >
                                                    Dispatch
                                                </t-button>
                                            </div>
                                            <div
                                                v-else-if="deliveryStatus == 'Shipped' && row.payment_method === 'COD'"
                                            >
                                                <t-button
                                                    variant="success"
                                                    size="sm"
                                                    @click="showPaymentCollectionModal(row)"
                                                >
                                                    Payment
                                                </t-button>
                                            </div>
                                            <div
                                                v-else-if="
                                                    deliveryStatus == 'Shipped' && row.payment_method === 'Charge'
                                                "
                                            >
                                                <t-button
                                                    variant="success"
                                                    size="sm"
                                                    @click="goto('bill.deliveredStatus', { bill_id: row.id })"
                                                >
                                                    Delivered
                                                </t-button>
                                            </div>
                                            <div v-else-if="deliveryStatus == 'Delivered'">
                                                <t-button
                                                    variant="success"
                                                    size="sm"
                                                    @click="goto('reports.show', row)"
                                                >
                                                    View
                                                </t-button>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                                <!-- Empty State -->
                                <tr
                                    v-if="!bills.data.length"
                                    class="text-center"
                                >
                                    <td
                                        colspan="7"
                                        class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                    >
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div
                        v-if="bills.data.length"
                        class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                    >
                        <t-pagination
                            v-model="currentPage"
                            variant="rounded"
                            :total-items="totalRows"
                            :per-page="perPage"
                            :limit="limit"
                            :disabled="!bills.data.length"
                            @change="fetchPageData"
                        />
                    </div>
                    <p class="sm:hidden text-xs text-center text-gray-500 py-2">
                        ↔ Swipe left/right to scroll the table
                    </p>
                </div>
            </div>

            <t-modal
                v-model="paymentModal"
                header="Payment Collection"
            >
                <ToDelivered
                    :bills="currentInvoice"
                    @closeModal="paymentModal = false"
                    @payment-complete="hidePaymentCollectionModal"
                />
            </t-modal>
        </div>
    </AppLayout>
</template>

<script>
    import _ from 'lodash';
    import AppLayout from '@/Layouts/AppLayout.vue';
    import common from '@/common';
    import ToDelivered from './ToDelivered.vue';

    export default {
        name: 'OrderBills',

        components: { AppLayout, ToDelivered },

        props: {
            bills: {
                type: Object,
                required: true,
            },
            deliveryStatus: {
                type: String,
                required: false,
                default: null,
            },
            paymentMethod: {
                type: String,
                required: false,
                default: null,
            },
            appliedFilters: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                paymentModal: false,
                currentInvoice: null,
                common,
                currentPage: null,
                pageTitle: null,
                limit: 5,
                perPage: this.bills.per_page,
                totalRows: this.bills.total,
                search: null,
                paymentType: null,
                mounted: false,
                disabled: false,
            };
        },

        watch: {
            paymentType() {
                if (this.mounted) {
                    this.fetchPageData(this.current_page);
                }
            },
        },

        created() {
            this.currentPage = this.bills.current_page;
        },

        mounted() {
            this.disabled = this.bills.data.length === 0;

            if (this.appliedFilters.keyword) {
                this.search = this.appliedFilters.keyword;
            }

            if (this.deliveryStatus === null) {
                this.pageTitle = 'All Orders';
            } else {
                this.pageTitle = _.capitalize(`${this.deliveryStatus}`);
            }
            if (this.paymentMethod) {
                this.paymentType = this.paymentMethod === 'Charge' ? 'ACCOUNT' : 'COD';
            } else {
                this.paymentType = 'BOTH';
            }

            this.$nextTick(() => {
                this.mounted = true;
            });
        },

        methods: {
            getTotalAmount(transactions) {
                const amount = transactions.reduce(
                    (previousValue, currentValue) => previousValue + parseFloat(currentValue.grand_total),
                    0,
                );
                return parseFloat(amount).toFixed(2);
            },

            showPaymentCollectionModal(invoice) {
                this.currentInvoice = invoice;
                this.paymentModal = true;
            },

            hidePaymentCollectionModal() {
                this.paymentModal = false;
                this.fetchPageData(this.current_page);
            },

            fetchPageData(newPage = 1) {
                this.$inertia.get(
                    route('bills.delivered', {
                        page: newPage,
                        keyword: this.search,
                        // eslint-disable-next-line no-nested-ternary
                        paymentMethod:
                            this.paymentType === 'BOTH' ? null : this.paymentType === 'ACCOUNT' ? 'Charge' : 'COD',
                    }),
                );
            },
        },
    };
</script>
